import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import pick from 'lodash/pick';
import Fuse from 'fuse.js';
import isNaN from 'lodash/isNaN';
import isEmpty from 'lodash/isEmpty';
import Currency from '$app/components/vendor/Currency';
import WorkOperationEditor, { ROW_TYPE } from '$app/components/vendor/WorkOperationEditor';
import { createGlobalStyle } from 'styled-components';
import { Popover, Icon, message } from 'antd';
import { applyVat, poundsToPence } from '$app/utils/util';
import {
  calculateCustomerLineItemPrices,
  getCustomerLabourRatePounds,
  prepareSelectedWorkOperationPricing,
} from '../../util';
import Markup from '../WorkDetailsTable/Markup';
import { QeTag } from './styled';
import { BOOKING_STATUS } from '$app/apps/bookings/containers/BookingPage/constants';

const markAsNull = (items) =>
  items.map((item) => ({
    ...item,
    price: item.price === 0 ? null : item.price,
    quantity: item.quantity === 0 ? null : item.quantity,
  }));

/**
 * Global styles are injected here to override the work operation table inside modal
 * Using standard styled-components doesn't work because of the css scoping
 */

const Styles = createGlobalStyle`
  body {
    .ant-modal {
      .work-operations-table {
        border-right: 1px solid #ebebeb;
        tr {
            :first-child th:nth-child(3),
            :nth-child(1) >:last-child,
            >:last-child,
            >:nth-child(6),
            >:nth-child(7),
            >:nth-child(8),
            >:nth-child(9) {
              background: #f6fafa;
            }
            :first-child th:nth-child(3) {
              border-top: 1px solid #ebebeb;
            }
            :first-child th:nth-child(3),
            >:nth-child(6) {
              border-left: 1px solid #ebebeb;
            }
          }
        }
        .ant-table-thead > tr:nth-child(2) > th:nth-child(6) > span {
          padding-left: 32px;
        }
        .total-row td:nth-child(6) > span,
        .total-row td:nth-child(6) > div {
          margin-right: -40px;
        }
        .total-row td:nth-child(6),
        .total-row td:nth-child(7) {
          background: #e8e8e8;
        }
      }
    }

    #T_WoEdit_SaveWo {
      width: initial !important;
    }
`;

/**
 * Used to wait for WorkOperationTable to mount
 */
const WorkOperationTableInstanceSymbol = Symbol('WorkOperationTableInstance');

/**
 * Note: Could be moved in columns declaration
 * */
const defaultLineItemData = Object.freeze({
  quantity: 0,
  price: 0,
  garageCost: 0,
  garageCostWithVat: 0,
});

const URGENCY_OPTIONS = Object.freeze({
  dangerous: {
    name: 'DANGEROUS',
    style: {
      backgroundColor: '#fae8eb',
      color: '#d51e3c',
    },
    description: 'MOT Fail - Do not drive the vehicle until it’s been repaired.',
  },
  major: {
    name: 'MAJOR',
    style: {
      backgroundColor: '#fae8eb',
      color: '#d51e3c',
    },
    description: 'MOT Fail - Repair it immediately.',
  },
  minor: {
    name: 'MINOR',
    style: {
      backgroundColor: '#fef8ee',
      color: '#fcbe56',
    },
    description: 'MOT Pass - Repair as soon as possible.',
  },
  advisory: {
    name: 'ADVISORY',
    style: {
      backgroundColor: '#fef8ee',
      color: '#fcbe56',
    },
    description: 'MOT Pass - Monitor and repair it if necessary.',
  },
  performance_maintenance: {
    name: 'PERFORMANCE MAINTENANCE',
    style: {
      backgroundColor: '#e6f7ff',
      color: '#1890ff',
    },
    description: 'Not required for MOT - Recommended for the vehicle performance.',
  },
  other: {
    name: 'CUSTOMER REQUEST',
    style: {
      backgroundColor: '#f4f4f4',
      color: '#92979c',
    },
    description: 'Customer request',
  },
});

const validator = (form, columnKey) => (rule, value, callback) => {
  const errors = [];
  if (['quantity', 'price'].includes(columnKey) && isNaN(parseFloat(value))) {
    errors.push(new Error('required'));
  }
  if (columnKey === 'workSummary' && isEmpty(value)) {
    errors.push(new Error('Please name the part'));
  }
  return callback(errors);
};

export default class WorkOperationEditorModal extends PureComponent {
  refWorkOperationEditor = React.createRef();

  [WorkOperationTableInstanceSymbol] = null;

  state = {
    workOperation: this.props.workOperation,
    saveDisabled: false,
    /*
     * If the customer or garage price already exists, don't
     * automatically apply 1.25% markup when editing the garage price
     * */
    didEditCustomerValues: get(this, 'props.workOperation.fixedCostWithVAT', 0) > 0,
    didEditGarageValues: get(this, 'props.workOperation.garageCostWithVat', 0) > 0,
    markup: pick(this.props.workOperation, ['fixedCostWithVAT', 'garageCostWithVat']),
  };

  isNew = !get(this.state.workOperation, 'key');

  get columns() {
    return [
      {
        title: '',
        key: 'summary-group',
        children: [
          {
            title: 'Quote',
            key: 'workSummary',
            editable: ({ key, type }) => key !== 'total' && type !== ROW_TYPE.labour,
            input: this.inputOptions.text,
          },
        ],
      },
      {
        title: 'Garage',
        key: 'garage-group',
        children: [
          {
            title: 'Qty',
            key: 'quantity',
            input: this.inputOptions.number.garage,
            align: 'right',
            editable: ({ key }) => key !== 'total',
            width: 108,
          },
          {
            title: 'Price',
            key: 'price',
            input: this.inputOptions.number.garage,
            displayPrice: true,
            align: 'right',
            editable: ({ key }) => key !== 'total',
            width: 108,
          },
          {
            title: 'Total (pre VAT)',
            key: 'garageCost',
            displayPrice: true,
            align: 'right',
            width: 108,
          },
          {
            title: 'Total (inc. VAT)',
            key: 'garageCostWithVat',
            displayPrice: true,
            align: 'right',
            width: 108,
          },
        ],
      },
      {
        title: 'Customer',
        key: 'customer-group',
        children: [
          {
            title: 'Inc. VAT',
            key: 'fixedCostWithVAT',
            input: this.inputOptions.number.customer,
            displayPrice: true,
            align: 'center',
            editable: ({ key }) => key !== 'total' && this.props.enabledCustomerPriceEdit,
            render: (...args) => {
              const { customerCostIsQuoteEngineGenerated, didEditCustomerValues } = this.state;
              const [customerPounds, { key, fixedCostWithVat }] = args;
              // Calculated prices live on fixedCostWithVAT (pounds). fixedCostWithVat is the
              // original price-list pence field and stays empty on oil and parts rows.
              const calculatedCustomerPence = Number(customerPounds) ? poundsToPence(Number(customerPounds)) : 0;
              const customerPence = calculatedCustomerPence || Number(fixedCostWithVat) || 0;
              if (this.state.editingRow === key || key === 'title' || key === 'total') {
                return 'default';
              }
              if (!this.isNew || (didEditCustomerValues && !customerCostIsQuoteEngineGenerated)) {
                return 'default';
              }
              if (!customerPence) {
                return (
                  <span
                    style={{
                      color: '#262626',
                      opacity: 0.2,
                      width: 156,
                      display: 'block',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon type="flag" theme="filled" /> Not calculated yet
                  </span>
                );
              }
              if (customerCostIsQuoteEngineGenerated) {
                return (
                  <Popover
                    content={
                      <QeTag>
                        <Icon type="check-circle" theme="filled" />
                        Provided by Quote Engine
                      </QeTag>
                    }
                  >
                    <span style={{ color: '#52c41b' }}>
                      <Currency trailingZero>{customerPence}</Currency>
                      &nbsp;
                      <Icon type="info-circle" theme="filled" />
                    </span>
                  </Popover>
                );
              }
              return (
                <Popover
                  content={
                    <span>
                      The customer price is 25% markups on garage price calculated with the default labour
                      rate.{' '}
                    </span>
                  }
                >
                  <span style={{ color: '#722ed1' }}>
                    <Currency trailingZero>{customerPence}</Currency>
                    &nbsp;
                    <Icon type="info-circle" theme="filled" />
                  </span>
                </Popover>
              );
            },
            width: 160,
          },
          {
            title: 'Markup',
            key: 'markup',
            width: 100,
            render: (val, wo) => {
              if (!wo.garageCostWithVat || !wo.fixedCostWithVAT) return null;
              return (
                <Markup
                  prefix=""
                  garageCostWithVat={wo.garageCostWithVat}
                  fixedCostWithVAT={wo.fixedCostWithVAT}
                />
              );
            },
          },
          {
            title: '',
            key: 'delete',
            width: 30,
          },
        ],
      },
    ];
  }

  get inputOptions() {
    return {
      text: {
        type: 'text',
        placeholder: "Part's name",
        autoFocus: true,
      },
      number: {
        garage: {
          type: 'number',
          min: 0,
          onChange: this.handleGarageLineItemNumberChange,
          onAfterChange: (...args) => {
            this.handleNumberAfterChange(...args);
            this.setState({ didEditGarageValues: true });
          },
        },
        customer: {
          type: 'number',
          min: 0,
          autoFocus: !this.isNew,
          onChange: this.handleCustomerLineItemNumberChange,
          onAfterChange: (editorState, totalRow, lineItem, meta = {}) => {
            this.handleNumberAfterChange(editorState, totalRow, lineItem, {
              ...meta,
              didEditCustomerValues: true,
            });
            this.setState({ didEditCustomerValues: true, customerCostIsQuoteEngineGenerated: false });
          },
        },
      },
    };
  }

  get fieldOptions() {
    const { disableCommentToCustomer } = this.props;
    return {
      urgency: {
        list: URGENCY_OPTIONS,
        disabled: !this.isNew,
        onChange: this.handleUrgencyChange,
      },
      workAutocomplete: {
        onSelect: this.handleWorkSelected,
        filterOption: this.handleFilterAutoComplete.bind(this),
      },
      workDescription: {
        onChange: this.handleWorkDescriptionChange,
      },
      commentForCM: {
        display: this.isProposedWorkOperation,
        disabled: this.isProposedWorkOperation,
      },
      commentForCustomer: {
        display: !disableCommentToCustomer && this.isProposedWorkOperation,
        disabled: disableCommentToCustomer,
      },
    };
  }

  get WorkOperationEditorInstance() {
    return this.refWorkOperationEditor.current;
  }

  get WorkOperationTableInstance() {
    if (this[WorkOperationTableInstanceSymbol]) {
      return Promise.resolve(this[WorkOperationTableInstanceSymbol]);
    }
    return new Promise((resolve) => {
      const tableMount = setInterval(() => {
        // try until table is mounted
        this[WorkOperationTableInstanceSymbol] = this.WorkOperationEditorInstance.WorkOperationTableInstance;
        if (this[WorkOperationTableInstanceSymbol]) {
          clearInterval(tableMount);
          resolve(this[WorkOperationTableInstanceSymbol]);
        }
      }, 100);
    });
  }

  get isProposedWorkOperation() {
    return get(this.state.workOperation, 'quoteType') === 'proposed';
  }

  get showDelete() {
    const { bookingStatus } = this.props;
    return (
      !this.isProposedWorkOperation &&
      (bookingStatus < BOOKING_STATUS.WORK_COMPLETE || bookingStatus === BOOKING_STATUS.GARAGE_DECLINED)
    );
  }

  get renderMarkup() {
    const { markup, isMissingValues } = this.state;
    const { fixedCostWithVAT, garageCostWithVat } = markup || {};
    return (
      <div
        style={{
          textAlign: 'center',
          fontSize: 12,
          color: '#c0c0c0f',
          width: 192,
          margin: '10px 0 0 auto',
          cursor: 'pointer',
        }}
      >
        {isMissingValues ? (
          <Popover
            content={
              <div style={{ maxWidth: 200 }}>
                The markup will be calculated after completing the quantity and price
              </div>
            }
          >
            Markup <Icon type="info-circle" theme="filled" />
          </Popover>
        ) : (
          <Markup prefix="Markup" garageCostWithVat={garageCostWithVat} fixedCostWithVAT={fixedCostWithVAT} />
        )}
      </div>
    );
  }

  handleFilterAutoComplete = (items, value) => {
    const groups = {
      REPAIRS: 'Repairs',
      DIAGNOSTICS: 'Diagnostics',
      MOT_AND_SERVICING: 'MOT & Servicing',
    };

    const repairs = get(
      items.find((i) => i.title === groups.REPAIRS),
      'products',
      []
    ).map((p) => ({ ...p, group: groups.REPAIRS }));

    const diagnostics = get(
      items.find((i) => i.title === groups.DIAGNOSTICS),
      'products',
      []
    ).map((p) => ({ ...p, group: groups.DIAGNOSTICS }));

    const motAndServicing = get(
      items.find((i) => i.title === groups.MOT_AND_SERVICING),
      'products',
      []
    ).map((p) => ({ ...p, group: groups.MOT_AND_SERVICING }));

    const options = {
      threshold: 0.5,
      keys: [
        'workSummary',
        'searchTerms',
        // 'text',
        // 'subLineItems.workSummary',
        // 'subLineItems.searchTerms',
        // 'subLineItems.text',
      ],
    };

    const typeOther = items.find((i) => i.custom);

    const fullSearch = new Fuse([...repairs, ...diagnostics, ...motAndServicing], options);

    const searchResult = fullSearch.search(value);

    const regroup = [
      {
        title: groups.REPAIRS,
        products: searchResult.map((i) => i.item).filter((i) => i.group === groups.REPAIRS),
      },
      {
        title: groups.DIAGNOSTICS,
        products: searchResult.map((i) => i.item).filter((i) => i.group === groups.DIAGNOSTICS),
      },
      {
        title: groups.MOT_AND_SERVICING,
        products: searchResult.map((i) => i.item).filter((i) => i.group === groups.MOT_AND_SERVICING),
      },
    ]; // .sort((a, b) => b.length - a.length); - to sort the group length, but this is overwritten by the @fxtr/work-operation-editor

    const finalResult = [...regroup, typeOther];

    return finalResult;
  };

  calculateGaragePrice = (lineItem) => {
    const { quantity, price, vatExempt } = lineItem;
    // It's impossible to have fractions of a penny
    // Wrong - (2.2 * £20.37) = £44.814 * 1.2 = £53.7768 ... £53.78
    // Correct - round(2.2 * 2037p) = 4481p * 1.2 = 5,377 / 100 = £53.77
    // If unsure, try it in Xero
    lineItem.garageCost = Math.round(quantity * price * 100) / 100;
    lineItem.garageCostWithVat = vatExempt ? lineItem.garageCost : applyVat(lineItem.garageCost);
    return lineItem;
  };

  get customerLabourRatePounds() {
    return getCustomerLabourRatePounds(this.props.labourRateForCustomerPence);
  }

  handleUrgencyChange = (urgency) => {
    this.props.onUrgencyChange(urgency);
  };

  handleWorkSelected = (wo) => {
    const {
      lineItems,
      workOperation,
      hasQuoteEngineCustomerPrices,
      isMissingValues,
      hasGarageCost,
    } = prepareSelectedWorkOperationPricing(wo, this.customerLabourRatePounds);

    this.setState({
      isMissingValues,
      markup: {
        garageCostWithVat: workOperation.garageCostWithVat,
        fixedCostWithVAT: workOperation.fixedCostWithVAT,
      },
      workOperation,
      didEditCustomerValues: hasQuoteEngineCustomerPrices,
      customerCostIsQuoteEngineGenerated: hasQuoteEngineCustomerPrices,
      didEditGarageValues: hasGarageCost,
    });
    this.WorkOperationEditorInstance.setNewLineItems(markAsNull(lineItems));
  };

  handleWorkDescriptionChange = (workSummary) => {
    this.props.onWorkDescriptionChange(workSummary);
  };

  // handleCommentChange = comment => {
  //   this.setState({ saveDisabled: !comment });
  //   this.props.onCommentChange(comment);
  // };

  handleGarageLineItemNumberChange = (lineItem) => {
    const li = this.calculateGaragePrice(lineItem);
    if (!this.state.didEditCustomerValues) {
      Object.assign(li, calculateCustomerLineItemPrices(li, this.customerLabourRatePounds));
    }
    if (this.isProposedWorkOperation && li.type === ROW_TYPE.labour) {
      this.setState({ saveDisabled: !li.garageCostWithVat });
    }
    return li;
  };

  handleCustomerLineItemNumberChange = (lineItem) => {
    lineItem.fixedCost = lineItem.fixedCostWithVAT / 1.2;
    return lineItem;
  };

  handleNumberAfterChange = (editorState, totalRow, _lineItem, meta = {}) => {
    const { workOperation } = this.state;
    const customerColumnEdited =
      Boolean(meta.didEditCustomerValues) || meta.columnKey === 'fixedCostWithVAT';
    const didEditCustomerValues = this.state.didEditCustomerValues || customerColumnEdited;
    const { garageCostWithVat } = totalRow;
    const isMissingValues = editorState.newLineItems.find(({ garageCost }) => !garageCost);
    const customerLabourRatePounds = this.customerLabourRatePounds;

    /** *
     * Calculate the customer total from the garage
     * price when the quote engine price is not
     * available and the fixed cost hasn't been entered manually
     */

    if (!didEditCustomerValues) {
      /** *
       * You can override the labour here. Garages now have "discountedLabourRate",
       * we cannot apply this to the customer price. This is also applied in garage routes.
       */

      const pricedLineItems = editorState.newLineItems.map((item) =>
        calculateCustomerLineItemPrices(item, customerLabourRatePounds)
      );
      pricedLineItems.forEach((item, index) => {
        Object.assign(editorState.newLineItems[index], pick(item, ['fixedCost', 'fixedCostWithVAT']));
      });

      const fixedCost = pricedLineItems.reduce((ac, i) => ac + (Number(i.fixedCost) || 0), 0);
      const fixedCostWithVAT = pricedLineItems.reduce((ac, i) => ac + (Number(i.fixedCostWithVAT) || 0), 0);

      workOperation.fixedCost = fixedCost;
      workOperation.fixedCostWithVAT = fixedCostWithVAT;

      return this.setState({
        isMissingValues,
        markup: { garageCostWithVat, fixedCostWithVAT: workOperation.fixedCostWithVAT },
        didEditGarageValues: true,
        workOperation,
      });
    }

    if (customerColumnEdited) {
      const editor = this.WorkOperationEditorInstance;
      const lineItems = (editor && editor.lineItems) || editorState.newLineItems || [];
      workOperation.fixedCost = lineItems.reduce((ac, i) => ac + (Number(i.fixedCost) || 0), 0);
      workOperation.fixedCostWithVAT = lineItems.reduce(
        (ac, i) => ac + (Number(i.fixedCostWithVAT) || 0),
        0
      );
    }

    this.setState({
      isMissingValues,
      markup: {
        garageCostWithVat,
        fixedCostWithVAT: workOperation.fixedCostWithVAT || totalRow.fixedCostWithVAT,
      },
      didEditGarageValues: true,
      workOperation,
    });
  };

  componentDidMount() {
    // set default customer price if it's missing
    if (!this.isNew && !this.props.workOperation.fixedCostWithVAT) {
      this.WorkOperationTableInstance.then((WorkOperationTableInstance) => {
        WorkOperationTableInstance.setEditingKey('total');
      });
    }
  }

  onLineItemDelete = (...args) => {
    const { onLineItemDelete } = this.props;
    /** *
     * Bug: Set timeout is needed to wait for WorkOperationEditorInstance to update correctly
     * If we read the instance values immediately, the markup will be calculated incorrectly
     *
     * TODO: Solve incorrect markup issue
     */
    setTimeout(() => {
      const { totalRow, lineItems = [] } = this.WorkOperationEditorInstance;
      const isMissingValues = lineItems.find(({ garageCost }) => !garageCost);
      onLineItemDelete(args);
      const { workOperation } = this.state;
      workOperation.fixedCost = lineItems.reduce((ac, i) => ac + (Number(i.fixedCost) || 0), 0);
      workOperation.fixedCostWithVAT = lineItems.reduce(
        (ac, i) => ac + (Number(i.fixedCostWithVAT) || 0),
        0
      );
      this.setState({
        isMissingValues,
        workOperation,
        markup: pick(totalRow, ['fixedCostWithVAT', 'garageCostWithVat']),
      });
    }, 0);
  };

  get workOperation() {
    const { workOperation } = this.state;
    return workOperation;
  }

  render() {
    const { onClose, onDelete, onSave, autocompleteList } = this.props;
    const { saveDisabled } = this.state;

    if (this.isNew && this.workOperation.sku && !this.workOperation.type) {
      message.error(
        <span>
          <b>{this.workOperation.workSummary}</b> is missing Accounting Type, add it in{' '}
          <b>
            Product {'>'} Product Pricing {'>'} Add a New Price
          </b>{' '}
          and try adding it again later
        </span>,
        [5]
      );
    }

    return (
      <>
        <Styles />
        <WorkOperationEditor
          ref={this.refWorkOperationEditor}
          columns={this.columns}
          workOperation={this.workOperation}
          defaultLineItemData={defaultLineItemData}
          fieldOptions={this.fieldOptions}
          calculateTotalColumns={['garageCost', 'garageCostWithVat', 'fixedCostWithVAT']}
          fixedTotalColumns={['fixedCostWithVAT']}
          saveDisabled={saveDisabled}
          showDelete={this.showDelete}
          renderUnderTable={this.renderMarkup}
          validator={validator}
          onLineItemDelete={this.onLineItemDelete}
          modalOkText={this.props.isCaseManagerReviewed ? null : 'Mark as Reviewed'}
          onSave={onSave}
          onDelete={onDelete}
          onClose={onClose}
          displayQuoteEnginetooltip
          autocompleteList={autocompleteList}
          isCommentMandatory={this.isProposedWorkOperation}
          editTotalRow
          onSetEditRow={(editingRow) => {
            this.setState({ editingRow });
          }}
        />
      </>
    );
  }
}

WorkOperationEditorModal.propTypes = {
  workOperation: PropTypes.object,
  onLineItemDelete: PropTypes.func,
  onClose: PropTypes.func,
  onDelete: PropTypes.func,
  onSave: PropTypes.func,
  autocompleteList: PropTypes.array,
  onUrgencyChange: PropTypes.func,
  onWorkDescriptionChange: PropTypes.func,
  onCommentChange: PropTypes.func,
  enabledCustomerPriceEdit: PropTypes.bool,
  isCaseManagerReviewed: PropTypes.bool,
  bookingStatus: PropTypes.number,
  disableCommentToCustomer: PropTypes.bool,
  labourRateForCustomerPence: PropTypes.number,
};

WorkOperationEditorModal.defaultProps = {
  workOperation: {},
  autocompleteList: [],
  enabledCustomerPriceEdit: false,
  isCaseManagerReviewed: true,
  bookingStatus: 10000,
  onLineItemDelete: () => {},
  onClose: () => {},
  onDelete: () => {},
  onSave: () => {},
  onUrgencyChange: () => {},
  onWorkDescriptionChange: () => {},
  onCommentChange: () => {},
  disableCommentToCustomer: true,
};
